import React, { createContext, ReactElement, useEffect, useState } from 'react';
import { Price } from 'src/components/UploadAndPrint/PriceDisplay';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { usePricingContext } from 'src/hooks/usePricingContext';
import { getPrice } from 'src/services/UploadAndPrintService';
import { currentCulture } from 'src/utils/cultureUtil';

export type PricingType = Price | null;

export interface PriceContextProps {
  price: PricingType;
}

export const PriceContext = createContext<PriceContextProps>({
  price: null,
});

export interface PriceContextProviderProps {
  children?: React.ReactNode;
}

export const PriceContextProvider = ({ children }: PriceContextProviderProps): ReactElement => {
  const { completedProductInformation } = useProductContext();
  const culture = currentCulture;
  const [price, setPrice] = useState<PricingType>(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const pricingContext = usePricingContext();

  useEffect(() => {
    let isStale = false;
    async function retrievePrice() {
      if (!completedProductInformation?.productKey) {
        return;
      }

      setLoadingPrice(true);
      if (pricingContext) {
        try {
          if (completedProductInformation) {
            const price = await getPrice(
              completedProductInformation.productKey,
              completedProductInformation.productVersion,
              culture,
              completedProductInformation.quantity,
              completedProductInformation.selectedOptions,
              pricingContext.vatInclusive
            );
            if (isStale) {
              return;
            }
            setPrice(price);
          }
        } catch (e) {
          console.error('Error getting price');
        } finally {
          setLoadingPrice(false);
        }
      }
    }
    retrievePrice();

    return () => {
      isStale = true;
    };
  }, [completedProductInformation, culture, pricingContext]);

  return (
    <PriceContext.Provider
      value={{
        price: loadingPrice ? null : price,
      }}
    >
      {children}
    </PriceContext.Provider>
  );
};
